import { useState } from 'react';
import { Box, Button, Group, SegmentedControl, SimpleGrid, Switch } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDownload } from '@tabler/icons-react';
import { DataTableColumn } from 'mantine-datatable';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { BillingDetail } from './hooks/useBilling';
import { useBillingDetails } from './hooks/useBillingDetails';
import BillingDetailModal from './BillingDetailModal';
import BillingEditModal from './BillingEditModal';
import {
  formatBillingMonth,
  formatPenceToPounds,
  getBillingTypeLabel,
  isBillingActive,
  nextBillingMonth,
  summariseMrr,
} from './billingUtils';
import { useBillingExport } from './hooks/useBillingExport';
import { HintedLabel } from './MetricHint';
import { SummaryCard } from './SummaryCard';

const columns: DataTableColumn<BillingDetail>[] = [
  { accessor: 'companyName', title: 'Company name', sortable: true },
  {
    accessor: 'billingType',
    title: 'Billing type',
    sortable: true,
    render: row => getBillingTypeLabel(row.billingType),
  },
  {
    accessor: 'billingAmount',
    title: <HintedLabel label="Billing amount (£)" hint="billingAmount" />,
    sortable: true,
    render: row =>
      row.billingAmount !== null && row.billingAmount !== undefined
        ? `£${Number(row.billingAmount).toFixed(2)}`
        : '-',
  },
  {
    accessor: 'billableUnitCount',
    title: <HintedLabel label="All units" hint="allUnits" />,
    sortable: true,
  },
  {
    accessor: 'activeBillableUnitCount',
    title: <HintedLabel label="Billed units" hint="billedUnits" />,
    sortable: true,
  },
  {
    accessor: 'statedClientCount',
    title: 'Stated clients',
    sortable: true,
    render: row =>
      row.statedClientCount === null ? '-' : row.statedClientCount.toLocaleString('en-GB'),
  },
  {
    accessor: 'monthlyBillPence',
    title: 'Monthly bill (£)',
    sortable: true,
    render: row => formatPenceToPounds(row.monthlyBillPence),
  },
  {
    accessor: 'billingStartDate',
    title: 'Start date',
    sortable: true,
    render: row => {
      if (!row.billingStartDate) return '-';
      const d = new Date(row.billingStartDate + 'T00:00:00');
      return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    },
  },
];

export default function BillingTab() {
  const [showInternal, setShowInternal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRow, setSelectedRow] = useState<BillingDetail | null>(null);
  const [editRow, setEditRow] = useState<BillingDetail | null>(null);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);

  const { data: billingData } = useBillingDetails();
  const { downloadExport, isLoading: isExporting } = useBillingExport();
  const billingMonthLabel = formatBillingMonth(nextBillingMonth());

  const summary = summariseMrr(billingData ?? [], showInternal);

  const handleRowClick = (row: BillingDetail) => {
    setSelectedRow(row);
    openModal();
  };

  const handleModalClose = () => {
    closeModal();
    setSelectedRow(null);
  };

  const handleEditClose = () => {
    closeEdit();
    setEditRow(null);
  };

  const columnsWithEdit: DataTableColumn<BillingDetail>[] = [
    ...columns,
    {
      accessor: 'edit',
      title: 'Actions',
      render: row => (
        <Button
          size="xs"
          variant="light"
          onClick={e => {
            e.stopPropagation();
            setEditRow(row);
            openEdit();
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <BillingDetailModal
        billingDetail={modalOpened ? selectedRow : null}
        onClose={handleModalClose}
      />
      <BillingEditModal billingDetail={editOpened ? editRow : null} onClose={handleEditClose} />

      <SimpleGrid cols={3} mb="md">
        <SummaryCard
          label="Active MRR"
          value={formatPenceToPounds(summary.active)}
          tooltip="Companies whose billing start date has passed"
        />
        <SummaryCard
          label="Pipeline MRR"
          value={formatPenceToPounds(summary.pending)}
          tooltip="Companies not yet billing (future or no start date)"
        />
        <SummaryCard
          label="Projected MRR"
          value={formatPenceToPounds(summary.total)}
          tooltip="Active + Pipeline — total once all companies are billing"
        />
      </SimpleGrid>

      <SimpleTable<BillingDetail>
        resource="admin/billing"
        columns={columnsWithEdit}
        title="Billing"
        showCount
        enableSearch
        pageSize={20}
        onRowClick={handleRowClick}
        filterPredicate={(item, search) => {
          if (!showInternal && item.isInternal) return false;
          if (statusFilter === 'active' && !isBillingActive(item.billingStartDate)) return false;
          if (statusFilter === 'pipeline' && isBillingActive(item.billingStartDate)) return false;
          if (!search.trim()) return true;
          const s = search.toLowerCase();
          return (
            item.companyName.toLowerCase().includes(s) || item.tenantName.toLowerCase().includes(s)
          );
        }}
        paginationDependencies={[showInternal, statusFilter]}
        titleSuffix={
          <Group gap="md">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconDownload size={14} />}
              loading={isExporting}
              onClick={() => downloadExport({ includeInternal: showInternal })}
            >
              Download {billingMonthLabel} billing
            </Button>
            <SegmentedControl
              size="xs"
              value={statusFilter}
              onChange={setStatusFilter}
              data={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'pipeline', label: 'Pipeline' },
              ]}
            />
            <Switch
              label="Show internal companies"
              checked={showInternal}
              onChange={e => setShowInternal(e.currentTarget.checked)}
            />
          </Group>
        }
      />
    </Box>
  );
}
